import { useState, useCallback, useMemo, useEffect, Fragment } from "react";
import {
  TrashIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/20/solid";
import Select from "react-select";
import AlertDialog from "./AlertDialog";

import CreatableSelect from "react-select/creatable";
import axios from "axios";
import BudgetItemsModal from "./ChangeOrderBudgetItemsModal";
import DatabaseItemsModal from "./ChangeOrderDatabaseItemsModal";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#4b5563",
    borderColor: state.isFocused ? "#f97316" : "#6b7280",
    color: "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "32px",
    fontSize: "12px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    border: "1px solid #4b5563",
    zIndex: 9999,
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 999999 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#6b7280" },
    padding: "6px 10px",
    fontSize: "12px",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#E5E7EB",
    fontSize: "12px",
  }),
  input: (provided) => ({ ...provided, color: "#E5E7EB", fontSize: "12px" }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9CA3AF",
    fontSize: "12px",
  }),
  valueContainer: (provided) => ({ ...provided, padding: "0 6px" }),
  indicatorsContainer: (provided) => ({
    ...provided,
    "& > div": { padding: "4px" },
  }),
};

const formatCurrency = (num) =>
  Number.isFinite(num)
    ? `$${num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : "$0.00";

const isSFUnit = (uom) => {
  const u = (uom || "").toLowerCase();
  return (
    u === "sf" || u.includes("square") || u.includes("sq ft") || u === "sheet"
  );
};

const isLFUnit = (uom) => {
  const u = (uom || "").toLowerCase();
  return u === "lf" || u.includes("linear") || u.includes("lin ft");
};

const isCoverageUnit = (uom) => isSFUnit(uom) || isLFUnit(uom);

const parseSizeToCoverage = (sizeStr, uom) => {
  if (!sizeStr) return null;
  const str = String(sizeStr).trim().toLowerCase();

  const parseLength = (part) => {
    if (!part) return 0;
    const mixedMatch = part.match(/^(\d+(?:\.\d+)?)' ?(\d+(?:\.\d+)?)"?$/);
    if (mixedMatch) {
      return (
        (parseFloat(mixedMatch[1]) || 0) + (parseFloat(mixedMatch[2]) || 0) / 12
      );
    }
    const feetMatch = part.match(/^([\d.]+)'?$/);
    if (feetMatch) return parseFloat(feetMatch[1]) || 0;
    const inchMatch = part.match(/^([\d.]+)"$/);
    if (inchMatch) return (parseFloat(inchMatch[1]) || 0) / 12;
    const num = parseFloat(part);
    return isNaN(num) ? 0 : num;
  };

  if (isSFUnit(uom)) {
    const parts = str.split(/\s*[xX×]\s*/);
    if (parts.length === 2) {
      const w = parseLength(parts[0]);
      const h = parseLength(parts[1]);
      if (w > 0 && h > 0) return w * h;
    }
    const area = parseLength(parts[0]);
    return area > 0 ? area : null;
  }

  if (isLFUnit(uom)) {
    const length = parseLength(str);
    return length > 0 ? length : null;
  }

  return null;
};

const isFromBudget = (item) => !!item.isFromBudget;

export default function TandMForm2({
  isEditing,
  editingId,
  formData,
  setFormData,
  generateTandMPDF,
  images,
  setImages,
  companyProfile,
  preparedBy,
  setAlert,
  vendorOptions,
  costCodeOptions,
  project,
  setTimeAndMaterials,
  setProject,
  setIsCreating,
  setIsEditing,
  setEditingId,
  setViewMode,
  handleCreateToggle,
}) {
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showDatabaseModal, setShowDatabaseModal] = useState(false);
  const [budgetItems, setBudgetItems] = useState([]);
  const [selectedBudgetItems, setSelectedBudgetItems] = useState([]);
  const [databaseItems, setDatabaseItems] = useState([]);
  const [netQuantities, setNetQuantities] = useState(new Map());
  const [netLoading, setNetLoading] = useState(false);
  const [loadingDatabase, setLoadingDatabase] = useState(false);
  const [selectedDatabaseItems, setSelectedDatabaseItems] = useState([]);
  const [databaseFilters, setDatabaseFilters] = useState({
    category: [],
    uom: [],
    search: "",
  });
  const [showAddItems, setShowAddItems] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [localAlert, setLocalAlert] = useState({
    open: false,
    type: "",
    title: "",
    message: "",
  });

  const items = formData.items || [];

  // ====================== ASSEMBLY + BID AREA OPTIONS (matching CO pattern) ======================
  const assemblyOptions = useMemo(() => {
    const unique = new Set();
    budgetItems.forEach((item) => {
      if (item.conditionName) unique.add(item.conditionName);
    });
    items.forEach((item) => {
      if (item.conditionName) unique.add(item.conditionName);
    });
    return [
      { value: "", label: "— Select Assembly —" },
      ...Array.from(unique)
        .sort()
        .map((v) => ({ value: v, label: v })),
    ];
  }, [budgetItems, items]);

  const bidAreaOptions = useMemo(() => {
    const unique = new Set();
    budgetItems.forEach((item) => {
      if (item.bidArea) unique.add(item.bidArea);
    });
    items.forEach((item) => {
      if (item.bidArea) unique.add(item.bidArea);
    });
    return [
      { value: "", label: "— Select Bid Area —" },
      ...Array.from(unique)
        .sort()
        .map((v) => ({ value: v, label: v })),
    ];
  }, [budgetItems, items]);

  // ====================== INLINE UPDATE HANDLER (matches CO handleUpdateItem exactly) ======================
  const handleUpdateItem = useCallback(
    (localId, field, value) => {
      setFormData((prev) => ({
        ...prev,
        items: (prev.items || []).map((item) => {
          if ((item._localId || item._id) !== localId) return item;

          const updated = { ...item };
          const isMaterial = updated.category === "M";

          if (field === "deltaQty" || field === "quantityUsed") {
            const raw = String(value).trim();

            // Allow intermediate typing states (no minus — T&M is positive only)
            if (raw === "" || raw === ".") {
              updated._rawDeltaInput = raw;
              updated.deltaQty = 0;
              updated.quantityUsed = 0;
              updated.totalCost = 0;
              updated.amount = 0;
              return updated;
            }

            const parsed = parseFloat(raw);
            if (isNaN(parsed)) return item; // reject non-numeric

            // Clear the raw input marker since we have a valid number
            delete updated._rawDeltaInput;

            // Snap: whole numbers for materials, decimals for labor
            let newDelta = isMaterial ? Math.round(parsed) : parsed;

            // T&M: only positive quantities allowed (no negatives)
            if (newDelta < 0) newDelta = 0;

            updated.deltaQty = newDelta;
            updated.quantityUsed = newDelta;
          } else if (field === "size") {
            updated.size = value;
            if (isMaterial && isCoverageUnit(updated.uom)) {
              const newCov = parseSizeToCoverage(value, updated.uom);
              if (newCov !== null) {
                updated.coverage = newCov;
                updated.coveragePerUnit = String(newCov);
              }
            }
          } else if (field === "unitCost") {
            updated.unitCost = parseFloat(value) || 0;
          } else if (field === "vendor") {
            updated.vendor = value?.value || null;
            updated._vendorOption = value;
          } else if (field === "bidArea") {
            // Matches CO: accepts raw string from onCreateOption or option.value from onChange
            updated.bidArea = value || "";
          } else if (field === "conditionName") {
            // Matches CO: accepts raw string from onCreateOption or option.value from onChange
            updated.conditionName = value || "";
          } else if (field === "forCustomer") {
            updated.forCustomer = value;
          } else if (field === "forBudget") {
            updated.forBudget = value;
          } else {
            updated[field] = value;
          }

          // Recalculate amounts (matching CO calculation)
          const qty = Math.abs(updated.deltaQty ?? updated.quantityUsed ?? 0);
          const coverage = parseFloat(updated.coveragePerUnit) || 1;
          const isCoverageItem = isMaterial && isCoverageUnit(updated.uom);
          const unitCost = updated.unitCost || 0;
          const costTotal = isCoverageItem
            ? qty * coverage * unitCost
            : qty * unitCost;

          updated.totalCost = costTotal;
          updated.amount = costTotal;

          return updated;
        }),
      }));
    },
    [setFormData],
  );

  // ====================== NET QUANTITIES ======================
  const fetchNetQuantities = useCallback(async () => {
    if (netQuantities.size > 0) return;
    try {
      setNetLoading(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const netRes = await axios.get(
        `${API_BASE}/net-assemblies/project-by-source/${project._id}`,
        {
          headers,
          params: { category: "all", includeClosed: "true", mode: "combined" },
        },
      );
      const {
        combined = [],
        originalItems = [],
        changeOrderItems = [],
      } = netRes.data;
      const allNetItems =
        combined.length > 0
          ? combined
          : [...originalItems, ...changeOrderItems];
      const newNetMap = new Map();
      const unifiedBudgetItems = [];

      allNetItems.forEach((netItem) => {
        const key = netItem.isNewSpec
          ? (netItem.lineItemId || netItem._id || netItem.key)?.toString()
          : (
              netItem.key ||
              netItem.originalLineItemId ||
              netItem.lineItemId ||
              netItem._id
            )?.toString();
        if (!key) return;

        const normalized = {
          ...netItem,
          _id: netItem.lineItemId || netItem._id || key,
          itemCode:
            netItem.itemCode || netItem.materialCode || netItem.laborCode || "",
          itemDescription:
            netItem.itemDescription || netItem.costCodeDescription || "",
          requiredQty: netItem.netRequiredQty || netItem.requiredQty || 0,
          remainingQty: netItem.netRemaining || 0,
          budgetedQty: netItem.baseRequiredQty || netItem.requiredQty || 0,
          unitCost: netItem.price || netItem.cost || netItem.unitRate || 0,
          latestPrice: netItem.latestPrice || netItem.price || 0,
          coveragePerUnit: netItem.coveragePerUnit || 1,
          size: netItem.latestSize || netItem.size || "",
          uom: netItem.uom || "",
          category: netItem.category || "M",
          conditionName: netItem.conditionName || "",
          bidArea: netItem.bidArea || "",
          laborId: netItem.laborId || null,
          vendor: netItem.vendor?._id || netItem.vendor || null,
          isHistorical:
            !!netItem.isHistorical || !!netItem.isHistoricalPriceLine,
          isSizeChangeCloseout:
            !!netItem.isSizeChangeCloseout || !!netItem.isSizeChangeCloseoutRow,
          isNewSpec: !!netItem.isNewSpec,
          hasPriceChange: !!netItem.hasPriceChange,
          closedOutBySizeChange: !!netItem.closedOutBySizeChange,
          rowTypeLabel: netItem.isNewSpec
            ? "New Spec (Size Change)"
            : netItem.closedOutBySizeChange
              ? "Closed Out (Size Change)"
              : netItem.isHistoricalPriceLine
                ? "Historical Price"
                : "Active",
        };
        newNetMap.set(key, normalized);
        unifiedBudgetItems.push(normalized);
      });

      setNetQuantities(newNetMap);
      setBudgetItems(unifiedBudgetItems);
    } catch (err) {
      console.error(err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Could not load current net budget quantities",
      });
    } finally {
      setNetLoading(false);
    }
  }, [project?._id]);

  const loadBudgetItems = async () => {
    await fetchNetQuantities();
  };

  useEffect(() => {
    if (project?._id) {
      fetchNetQuantities();
    }
  }, [project?._id, fetchNetQuantities]);

  const loadDatabaseItems = async () => {
    setLoadingDatabase(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/item-database?limit=5000`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDatabaseItems(res.data.items || []);
    } catch (err) {
      console.error(err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to load database items",
      });
    } finally {
      setLoadingDatabase(false);
    }
  };

  // ====================== ADD BUDGET ITEMS ======================
  const addBudgetItems = () => {
    if (selectedBudgetItems.length === 0) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Please select at least one item",
      });
      return;
    }

    const newItems = selectedBudgetItems
      .map((item, idx) => {
        const itemKey = (
          item.originalLineItemId ||
          item.lineItemId ||
          item._id ||
          item.key
        )?.toString();
        const netInfo = netQuantities.get(itemKey) || item;
        const remaining = netInfo.netRemaining ?? item.remainingQty ?? 0;
        const budgeted =
          netInfo.netRequiredQty ??
          item.budgetedQty ??
          item.requiredQty ??
          remaining;

        // T&M: always positive quantities
        const qty = Math.max(0, Math.abs(parseFloat(item.deltaQty) || 1));
        const isMaterial = item.category === "M";
        let baseCost =
          netInfo.latestPrice ??
          (isMaterial
            ? item.cost || item.price
            : item.unitRate || item.cost || item.price) ??
          0;
        const unitCost = Number(baseCost.toFixed(2));
        let coverage =
          netInfo.latestCoveragePerUnit ??
          parseFloat(item.coveragePerUnit) ??
          1;
        let displaySize = netInfo.latestSize || item.size || "";
        if (isMaterial && coverage === 1 && isCoverageUnit(item.uom)) {
          displaySize = isSFUnit(item.uom) ? "1' x 1'" : "1'";
        }
        const baseAmount = isMaterial
          ? qty * coverage * unitCost
          : qty * unitCost;

        return {
          _localId: `budget-${Date.now()}-${idx}`,
          itemNumber: String((items.length || 0) + idx + 1),
          sourceAssemblyId: item.assemblyId,
          sourceLineItemId: itemKey,
          isFromBudget: true,
          itemCode:
            item.itemCode || item.materialCode || item.laborCode || item._id,
          category: item.category,
          sectionNumber: item.sectionNumber,
          title: item.itemDescription || item.costCodeDescription || "",
          description: item.itemDescription || item.costCodeDescription || "",
          customerDescription:
            item.itemDescription || item.costCodeDescription || "",
          conditionName: item.conditionName || "",
          bidArea: item.bidArea || "",
          deltaQty: qty,
          quantityUsed: qty,
          unitCost,
          totalCost: baseAmount,
          amount: baseAmount,
          size: displaySize,
          uom: item.uom || "",
          coveragePerUnit: String(coverage),
          coverage,
          vendor: item.vendor?._id || item.vendor || null,
          remainingQty: remaining,
          budgetedQty: budgeted,
          forCustomer: true,
          forBudget: true,
          referenceNumber: null,
          hasPriceChange:
            netInfo.hasPriceChange || item.hasPriceChange || false,
          originalPrice: netInfo.originalPrice ?? unitCost,
          isSizeChangeCloseout: item.isSizeChangeCloseout,
          isNewSpec: item.isNewSpec,
          isHistorical: item.isHistorical,
          rowTypeLabel: item.rowTypeLabel,
        };
      })
      .filter(Boolean);

    setFormData((prev) => ({
      ...prev,
      items: [...(prev.items || []), ...newItems],
    }));
    setSelectedBudgetItems([]);
    setShowBudgetModal(false);
    setShowAddItems(false);
    if (newItems.length > 0) {
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: `Added ${newItems.length} current budget item(s)`,
      });
    }
  };

  // ====================== ADD DATABASE ITEMS ======================
  const addDatabaseItems = () => {
    if (!selectedDatabaseItems || selectedDatabaseItems.length === 0) {
      setAlert({
        open: true,
        type: "error",
        title: "No Selection",
        message: "Please select at least one item.",
      });
      return;
    }

    const newItems = selectedDatabaseItems
      .map((sel) => {
        const item = databaseItems.find((d) => d.itemCode === sel.itemCode);
        if (!item) return null;

        const qty = Math.abs(sel.deltaQty || 1);
        const isMaterial = item.category === "M";

        let unitCost = item.defaultCost || 0;
        if (sel.selectedVendor && Array.isArray(item.vendorPrices)) {
          const vp = item.vendorPrices.find(
            (p) => (p.vendor?._id || p.vendor) === sel.selectedVendor,
          );
          if (vp?.price) unitCost = vp.price;
        }

        let size = sel.editedSize?.trim() || item.size?.trim() || "";
        let coverage;
        if (sel.editedCoverage && sel.editedCoverage !== 1) {
          coverage = sel.editedCoverage;
        } else if (isMaterial && isCoverageUnit(item.uom) && size) {
          const parsed = parseSizeToCoverage(size, item.uom);
          coverage =
            parsed !== null ? parsed : parseFloat(item.coveragePerUnit) || 1;
        } else {
          coverage = parseFloat(item.coveragePerUnit) || 1;
        }

        if (!size && isMaterial && isCoverageUnit(item.uom) && coverage === 1) {
          size = isSFUnit(item.uom) ? "1' x 1'" : "1'";
        }

        const totalCost = isMaterial
          ? qty * coverage * unitCost
          : qty * unitCost;

        return {
          _localId: `db-${Date.now()}-${sel.itemCode}`,
          itemNumber: String(
            (items.length || 0) + selectedDatabaseItems.indexOf(sel) + 1,
          ),
          itemCode: item.itemCode,
          isFromDatabase: true,
          category: item.category,
          sectionNumber: item.sectionNumber || "",
          title: item.description || "Unnamed Item",
          description: item.description || "Unnamed Item",
          customerDescription: item.description || "",
          conditionName: sel.conditionName || "",
          bidArea: sel.bidArea || "",
          deltaQty: qty,
          quantityUsed: qty,
          unitCost,
          totalCost,
          amount: totalCost,
          size,
          uom: item.uom || "EA",
          coveragePerUnit: String(coverage),
          coverage,
          vendor: isMaterial ? sel.selectedVendor : null,
          forCustomer: true,
          forBudget: true,
          referenceNumber: null,
        };
      })
      .filter(Boolean);

    setFormData((prev) => ({
      ...prev,
      items: [...(prev.items || []), ...newItems],
    }));
    setSelectedDatabaseItems([]);
    setShowDatabaseModal(false);
    setShowAddItems(false);
  };

  const handleDeleteItem = (localId) => {
    setFormData((prev) => ({
      ...prev,
      items: (prev.items || []).filter(
        (item) => (item._localId || item._id) !== localId,
      ),
    }));
  };

  const getDatabaseFilterOptions = useCallback(
    (field) => {
      const unique = new Set();
      databaseItems.forEach((item) => {
        const value = field === "uom" ? item.uom : item[field];
        if (value) unique.add(value);
      });
      return Array.from(unique)
        .sort()
        .map((v) => ({ value: v, label: v }));
    },
    [databaseItems],
  );

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImages((prev) => [...prev, ...files]);
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleRowExpanded = (localId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(localId)) next.delete(localId);
      else next.add(localId);
      return next;
    });
  };

  // Helper to get vendor option for a given item
  const getVendorOption = (item) => {
    if (item._vendorOption) return item._vendorOption;
    if (!item.vendor) return null;
    return vendorOptions?.find((o) => o.value === item.vendor) || null;
  };

  // ====================== COMPUTED TOTALS ======================
  const { budgetTotal, materialCount, laborCount } = useMemo(() => {
    let bTotal = 0;
    let mCount = 0;
    let lCount = 0;
    items.forEach((item) => {
      if (item.forBudget !== false) {
        bTotal += item.totalCost || item.amount || 0;
      }
      if (item.category === "M") mCount++;
      else lCount++;
    });
    return { budgetTotal: bTotal, materialCount: mCount, laborCount: lCount };
  }, [items]);

  // ====================== VALIDATION ======================
  const validateItems = () => {
    const missing = items.filter(
      (item) => !item.bidArea?.trim() || !item.conditionName?.trim(),
    );
    if (missing.length > 0) {
      const missingList = missing
        .map(
          (item, idx) =>
            `${idx + 1}) ${item.itemCode || "—"} - ${(item.description || item.customerDescription || "no desc").slice(0, 40)}`,
        )
        .join("\n");
      setLocalAlert({
        // ← was setAlert
        open: true,
        type: "error",
        title: "Missing Required Fields",
        message: `${missing.length} item(s) are missing Bid Area or Assembly:\n\n${missingList}`,
      });
      return false;
    }
    return true;
  };
  // ====================== SUBMIT ======================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!items || items.length === 0) {
      setLocalAlert({
        open: true,
        type: "error",
        title: "No Items",
        message: "Add at least one item.",
      });
      return;
    }
    if (!formData.signedBy?.trim()) {
      setLocalAlert({
        open: true,
        type: "error",
        title: "Missing",
        message: "Approved By name required.",
      });
      return;
    }
    if (!validateItems()) return;
    const formDataToSend = new FormData();
    formDataToSend.append(
      "items",
      JSON.stringify(
        items.map(({ _localId, _rawDeltaInput, _vendorOption, ...i }) => ({
          ...i,
          forCustomer: i.forCustomer !== false,
          forBudget: i.forBudget !== false,
        })),
      ),
    );
    formDataToSend.append("scopeOfWork", formData.scopeOfWork || "");
    formDataToSend.append("signedBy", formData.signedBy);
    formDataToSend.append(
      "overheadPercentage",
      formData.overheadPercentage || 0,
    );

    if (!isEditing && formData.signature instanceof File) {
      formDataToSend.append("signature", formData.signature);
    }
    images.forEach((img) => {
      if (img instanceof File) formDataToSend.append("images", img);
    });

    try {
      const token = localStorage.getItem("token");
      let response;
      if (isEditing) {
        response = await axios.put(
          `${API_BASE}/time-and-material2/${project._id}/${editingId}`,
          formDataToSend,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setTimeAndMaterials((prev) =>
          prev.map((t) => (t._id === editingId ? response.data : t)),
        );
      } else {
        response = await axios.post(
          `${API_BASE}/time-and-material2/signed/${project._id}`,
          formDataToSend,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setTimeAndMaterials((prev) => [...prev, response.data]);
        setProject((prev) => ({
          ...prev,
          timeAndMaterials2: [
            ...(prev.timeAndMaterials2 || []),
            response.data._id,
          ],
        }));
      }

      setIsCreating(false);
      setIsEditing(false);
      setEditingId(null);
      setViewMode("list");
      setFormData({
        items: [],
        scopeOfWork: "",
        signature: null,
        signedBy: "",
        overheadPercentage: companyProfile?.defaultOverhead || 0,
      });
      setImages([]);
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: `T&M ${isEditing ? "updated" : "created"}!`,
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to save.",
      });
    }
  };

  // ====================== RENDER ======================
  return (
    <div className="bg-gray-700 p-6 rounded-lg border border-gray-600">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white">
            {isEditing ? "Edit T&M Entry" : "New T&M Entry"}
          </h3>
          <p className="text-sm text-gray-400 mt-1">
            Descriptions are locked to item codes. Assembly and Bid Area are
            required.
          </p>
        </div>
        {isEditing && (
          <button
            onClick={() => generateTandMPDF(formData)}
            className="px-4 py-2 text-sm font-medium text-gray-200 bg-orange-400 rounded-md hover:bg-orange-500 transition-colors"
          >
            Download PDF
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* ====================== ITEMS SECTION ====================== */}
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-3">
                <h4 className="text-md font-semibold text-white">Items</h4>
                <span className="text-xs text-gray-400">
                  ({items.length} items
                  {materialCount > 0 && ` · ${materialCount} material`}
                  {laborCount > 0 && ` · ${laborCount} labor`})
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowAddItems(!showAddItems)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-200 bg-orange-500 rounded-md hover:bg-orange-600 transition-colors"
              >
                <PlusIcon className="h-4 w-4" /> Add Items
              </button>
            </div>

            {/* Add Items Panel */}
            {showAddItems && (
              <div className="bg-gray-700 rounded-lg p-5 mb-4 border border-gray-500">
                <div className="flex gap-4 justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      loadBudgetItems();
                      setShowBudgetModal(true);
                    }}
                    className="flex-1 max-w-xs px-6 py-4 text-base font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 shadow-lg transition-colors"
                  >
                    <div className="text-base font-semibold">
                      Add from Budget
                    </div>
                    <div className="text-xs opacity-75 mt-1">
                      Existing project items
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      loadDatabaseItems();
                      setShowDatabaseModal(true);
                    }}
                    className="flex-1 max-w-xs px-6 py-4 text-base font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 shadow-lg transition-colors"
                  >
                    <div className="text-base font-semibold">
                      Add from Database
                    </div>
                    <div className="text-xs opacity-75 mt-1">
                      Item database lookup
                    </div>
                  </button>
                </div>
                <div className="flex justify-end mt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddItems(false)}
                    className="px-4 py-2 text-sm text-gray-300 bg-gray-600 rounded hover:bg-gray-500 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {/* ====================== ITEMS TABLE ====================== */}
            {items.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-600">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider min-w-[200px]">
                        Description
                      </th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Cat
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider min-w-[160px]">
                        Assembly
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider min-w-[140px]">
                        Bid Area
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Size
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Unit Cost
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Unit
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Pkg Cost
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Qty Δ
                      </th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Pkg Unit
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                        Total Cost
                      </th>
                      <th
                        className="px-3 py-3 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider"
                        title="Include in customer PDF"
                      >
                        Cust
                      </th>
                      <th
                        className="px-3 py-3 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider"
                        title="Add to budget on approval"
                      >
                        Bdgt
                      </th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider w-16"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {items.map((item, index) => {
                      const localId = item._localId || item._id || index;
                      const isMaterial = item.category === "M";
                      const isCoverageItem =
                        isMaterial && isCoverageUnit(item.uom);
                      const isExpanded = expandedRows.has(localId);
                      const coverage =
                        parseFloat(item.coveragePerUnit) || item.coverage || 1;
                      const pkgCost = isCoverageItem
                        ? (item.unitCost || 0) * coverage
                        : item.unitCost || 0;
                      const total = item.totalCost || item.amount || 0;

                      return (
                        <Fragment key={localId}>
                          {/* Main Row */}
                          <tr
                            className={`hover:bg-gray-600 transition-colors ${isExpanded ? "bg-gray-750" : ""}`}
                          >
                            {/* Description - READ ONLY (linked to itemCode) */}
                            <td className="px-2 py-2 text-gray-300">
                              {item.customerDescription ||
                                item.description ||
                                "—"}
                            </td>

                            {/* Category Badge */}
                            <td className="px-2 py-2">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  isMaterial
                                    ? "bg-blue-500/20 text-blue-300"
                                    : "bg-purple-500/20 text-purple-300"
                                }`}
                              >
                                {isMaterial ? "Mat" : "Lab"}
                              </span>
                            </td>

                            {/* Assembly - locked for budget items, CreatableSelect for database items */}
                            <td className="px-2 py-2">
                              {isFromBudget(item) ? (
                                <span className="text-sm text-gray-300">
                                  {item.conditionName || "—"}
                                </span>
                              ) : (
                                <CreatableSelect
                                  options={assemblyOptions}
                                  value={
                                    assemblyOptions.find(
                                      (o) => o.value === item.conditionName,
                                    ) ||
                                    (item.conditionName
                                      ? {
                                          value: item.conditionName,
                                          label: item.conditionName,
                                        }
                                      : null)
                                  }
                                  onChange={(newOption) =>
                                    handleUpdateItem(
                                      localId,
                                      "conditionName",
                                      newOption ? newOption.value : "",
                                    )
                                  }
                                  onCreateOption={(inputValue) =>
                                    handleUpdateItem(
                                      localId,
                                      "conditionName",
                                      inputValue,
                                    )
                                  }
                                  placeholder="Type or select assembly..."
                                  isClearable
                                  formatCreateLabel={(inputValue) =>
                                    `Create new assembly: "${inputValue}"`
                                  }
                                  className="min-w-48"
                                  styles={customStyles}
                                  menuPortalTarget={document.body}
                                />
                              )}
                            </td>

                            {/* Bid Area - locked for budget items, CreatableSelect for database items */}
                            <td className="px-2 py-2">
                              {isFromBudget(item) ? (
                                <span className="text-sm text-gray-300">
                                  {item.bidArea || "—"}
                                </span>
                              ) : (
                                <CreatableSelect
                                  options={bidAreaOptions}
                                  value={
                                    bidAreaOptions.find(
                                      (o) => o.value === item.bidArea,
                                    ) ||
                                    (item.bidArea
                                      ? {
                                          value: item.bidArea,
                                          label: item.bidArea,
                                        }
                                      : null)
                                  }
                                  onChange={(newOption) =>
                                    handleUpdateItem(
                                      localId,
                                      "bidArea",
                                      newOption ? newOption.value : "",
                                    )
                                  }
                                  onCreateOption={(inputValue) =>
                                    handleUpdateItem(
                                      localId,
                                      "bidArea",
                                      inputValue,
                                    )
                                  }
                                  placeholder="Type or select bid area..."
                                  isClearable
                                  formatCreateLabel={(inputValue) =>
                                    `Create new bid area: "${inputValue}"`
                                  }
                                  className="min-w-48"
                                  styles={customStyles}
                                  menuPortalTarget={document.body}
                                />
                              )}
                            </td>

                            {/* Size - editable for SF/LF materials */}
                            <td className="px-2 py-2">
                              {isCoverageItem ? (
                                <input
                                  type="text"
                                  value={item.size || ""}
                                  onChange={(e) =>
                                    handleUpdateItem(
                                      localId,
                                      "size",
                                      e.target.value,
                                    )
                                  }
                                  placeholder={
                                    isSFUnit(item.uom) ? "4'x8'" : "10'"
                                  }
                                  className="w-24 rounded bg-gray-600 border-gray-500 text-gray-200 text-sm px-2 py-1 font-mono"
                                />
                              ) : isCoverageItem ? (
                                <span className="text-xs text-gray-300 font-mono">
                                  {item.size || "-"}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-500">-</span>
                              )}
                            </td>

                            {/* Unit Cost */}
                            <td className="px-2 py-2 text-right">
                              <div className="flex flex-col items-end">
                                <span
                                  className={`text-sm ${
                                    item.hasPriceChange
                                      ? "text-purple-400"
                                      : "text-gray-300"
                                  }`}
                                >
                                  {formatCurrency(item.unitCost)}
                                </span>
                                {item.hasPriceChange &&
                                  item.originalPrice !== item.unitCost && (
                                    <span className="text-[10px] text-gray-500 line-through">
                                      {formatCurrency(item.originalPrice)}
                                    </span>
                                  )}
                              </div>
                            </td>

                            {/* Unit (UOM) */}
                            <td className="px-2 py-2 text-right text-sm text-gray-300">
                              {item.uom}
                            </td>

                            {/* Pkg Cost = unitCost * coverage */}
                            <td className="px-2 py-2 text-right">
                              <span className="text-sm font-medium text-orange-300">
                                {formatCurrency(pkgCost)}
                              </span>
                            </td>

                            {/* Qty Δ - positive only for T&M */}
                            <td className="px-2 py-2">
                              <div className="flex flex-col gap-0.5">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={
                                    item._rawDeltaInput !== undefined
                                      ? item._rawDeltaInput
                                      : item.deltaQty || 0
                                  }
                                  onChange={(e) =>
                                    handleUpdateItem(
                                      localId,
                                      "deltaQty",
                                      e.target.value,
                                    )
                                  }
                                  onBlur={() => {
                                    if (
                                      item._rawDeltaInput !== undefined ||
                                      item.deltaQty === ""
                                    ) {
                                      handleUpdateItem(
                                        localId,
                                        "deltaQty",
                                        "0",
                                      );
                                    }
                                  }}
                                  className="w-20 px-2 py-1 text-right rounded border text-sm font-medium text-gray-200 bg-gray-600 border-gray-500"
                                />
                              </div>
                            </td>

                            {/* Pkg Unit - "PC" for coverage items, otherwise UOM */}
                            <td className="px-2 py-2 text-center">
                              <span className="text-xs text-gray-300">
                                {isCoverageItem ? "PC" : item.uom || "EA"}
                              </span>
                            </td>

                            {/* Total Cost */}
                            <td className="px-2 py-2 text-right">
                              <span className="text-sm font-medium text-gray-200">
                                {formatCurrency(total)}
                              </span>
                            </td>

                            {/* Customer checkbox (for PDF generation) */}
                            <td className="px-2 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={item.forCustomer !== false}
                                onChange={(e) =>
                                  handleUpdateItem(
                                    localId,
                                    "forCustomer",
                                    e.target.checked,
                                  )
                                }
                                className="w-4 h-4 rounded border-gray-500 text-orange-500 focus:ring-orange-500 cursor-pointer"
                                title="Include in customer PDF"
                              />
                            </td>

                            {/* Budget checkbox */}
                            <td className="px-2 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={item.forBudget !== false}
                                onChange={(e) =>
                                  handleUpdateItem(
                                    localId,
                                    "forBudget",
                                    e.target.checked,
                                  )
                                }
                                className="w-4 h-4 rounded border-gray-500 text-orange-500 focus:ring-orange-500 cursor-pointer"
                                title="Add to budget on approval"
                              />
                            </td>

                            {/* Actions */}
                            <td className="px-2 py-2">
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => toggleRowExpanded(localId)}
                                  className="text-gray-400 hover:text-orange-400 transition-colors p-0.5"
                                  title="Expand details"
                                >
                                  {isExpanded ? (
                                    <ChevronUpIcon className="h-4 w-4" />
                                  ) : (
                                    <ChevronDownIcon className="h-4 w-4" />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteItem(localId)}
                                  className="text-red-400 hover:text-red-300 transition-colors p-0.5"
                                  title="Remove item"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* Expanded Detail Row - Vendor + Item Info */}
                          {isExpanded && (
                            <tr className="bg-gray-750">
                              <td colSpan={14} className="px-4 py-3">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  {/* Vendor (material only) */}
                                  <div>
                                    <label className="block text-xs text-gray-400 mb-1">
                                      Vendor
                                      {!isMaterial && (
                                        <span className="text-gray-600 ml-1">
                                          (N/A for labor)
                                        </span>
                                      )}
                                    </label>
                                    {isMaterial ? (
                                      <Select
                                        options={vendorOptions}
                                        value={getVendorOption(item)}
                                        onChange={(opt) =>
                                          handleUpdateItem(
                                            localId,
                                            "vendor",
                                            opt,
                                          )
                                        }
                                        styles={customStyles}
                                        menuPortalTarget={document.body}
                                        isClearable
                                        placeholder="Select vendor..."
                                      />
                                    ) : (
                                      <div className="text-sm text-gray-500 py-1">
                                        —
                                      </div>
                                    )}
                                  </div>

                                  {/* Item Info */}
                                  <div>
                                    <label className="block text-xs text-gray-400 mb-1">
                                      Item Info
                                    </label>
                                    <div className="text-xs text-gray-400 space-y-0.5">
                                      <div>
                                        Code:{" "}
                                        <span className="text-gray-300 font-mono">
                                          {item.itemCode || "—"}
                                        </span>
                                      </div>
                                      {isFromBudget(item) && (
                                        <div>
                                          Source:{" "}
                                          <span className="text-orange-400">
                                            Budget
                                          </span>
                                        </div>
                                      )}
                                      {item.rowTypeLabel &&
                                        item.rowTypeLabel !== "Active" && (
                                          <div>
                                            Status:{" "}
                                            <span className="text-yellow-400">
                                              {item.rowTypeLabel}
                                            </span>
                                          </div>
                                        )}
                                    </div>
                                  </div>

                                  {/* Budget Info */}
                                  {isFromBudget(item) && (
                                    <div>
                                      <label className="block text-xs text-gray-400 mb-1">
                                        Budget Quantities
                                      </label>
                                      <div className="text-xs text-gray-400 space-y-0.5">
                                        <div>
                                          Budgeted:{" "}
                                          <span className="text-gray-300">
                                            {item.budgetedQty || 0}
                                          </span>
                                        </div>
                                        <div>
                                          Remaining:{" "}
                                          <span className="text-gray-300">
                                            {item.remainingQty || 0}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-10 text-gray-400">
                <p className="text-base">No items added yet</p>
                <p className="text-sm mt-1">Click "Add Items" to get started</p>
              </div>
            )}
          </div>

          {/* ====================== TOTALS SUMMARY ====================== */}
          {items.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex justify-end">
                <div className="bg-gray-600 p-3 rounded">
                  <p className="text-xs text-gray-400">Budget Total</p>
                  <p className="text-lg font-semibold text-green-400">
                    {formatCurrency(budgetTotal)}
                  </p>
                  <p className="text-[10px] text-gray-500">
                    Items marked for budget — added on approval
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ====================== SCOPE OF WORK ====================== */}
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">
              Scope of Work
            </label>
            <textarea
              value={formData.scopeOfWork || ""}
              onChange={(e) =>
                setFormData((p) => ({ ...p, scopeOfWork: e.target.value }))
              }
              rows="4"
              className="w-full p-3 rounded-md bg-gray-600 border border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors"
              placeholder="Describe the work completed..."
            />
          </div>

          {/* ====================== IMAGES ====================== */}
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">
              Upload Images
            </label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageUpload}
              className="block w-full text-sm text-gray-200 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-gray-200 hover:file:bg-gray-500 file:cursor-pointer"
            />
            {images.length > 0 && (
              <div className="mt-4 grid grid-cols-4 gap-3">
                {images.map((img, i) => (
                  <div key={i} className="relative group">
                    <img
                      src={
                        typeof img === "string" ? img : URL.createObjectURL(img)
                      }
                      alt={`Preview ${i + 1}`}
                      className="w-full h-28 object-cover rounded-lg border border-gray-600"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(i)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <TrashIcon className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ====================== SIGNATURE ====================== */}
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">
              Signature
            </label>
            {isEditing &&
            formData.signature &&
            typeof formData.signature === "string" ? (
              <img
                src={formData.signature}
                alt="Signature"
                className="w-48 h-32 object-contain bg-white rounded-lg border border-gray-600"
              />
            ) : (
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    signature: e.target.files[0] || null,
                  }))
                }
                className="block w-full text-sm text-gray-200 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-gray-200 hover:file:bg-gray-500 file:cursor-pointer"
              />
            )}
          </div>

          {/* ====================== APPROVED BY ====================== */}
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">
              Approved By (Name)
            </label>
            <input
              type="text"
              value={formData.signedBy || ""}
              onChange={(e) =>
                setFormData((p) => ({ ...p, signedBy: e.target.value }))
              }
              className="w-full p-3 rounded-md bg-gray-600 border border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none transition-colors"
              placeholder="Enter name of person approving"
              required
            />
          </div>

          {/* ====================== FORM ACTIONS ====================== */}
          <div className="flex justify-between pt-6 border-t border-gray-600">
            <button
              type="button"
              onClick={handleCreateToggle}
              className="px-6 py-3 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-8 py-3 text-sm font-medium text-white bg-orange-500 rounded-md hover:bg-orange-600 shadow-lg transition-colors"
            >
              {isEditing ? "Update Entry" : "Save Entry"}
            </button>
          </div>
        </div>
      </form>

      {/* ====================== MODALS ====================== */}
      <AlertDialog
        open={localAlert.open}
        type={localAlert.type}
        title={localAlert.title}
        message={localAlert.message}
        confirmText="OK"
        onClose={() =>
          setLocalAlert({ open: false, type: "", title: "", message: "" })
        }
        onConfirm={() =>
          setLocalAlert({ open: false, type: "", title: "", message: "" })
        }
      />
      <BudgetItemsModal
        open={showBudgetModal}
        onClose={() => {
          setShowBudgetModal(false);
          setSelectedBudgetItems([]);
        }}
        budgetItems={budgetItems}
        loading={netLoading}
        selectedItems={selectedBudgetItems}
        setSelectedItems={setSelectedBudgetItems}
        onAddItems={addBudgetItems}
        formatCurrency={formatCurrency}
        getFilterOptions={() => []}
        netQuantities={netQuantities}
        netLoading={netLoading}
      />

      <DatabaseItemsModal
        open={showDatabaseModal}
        onClose={() => {
          setShowDatabaseModal(false);
          setSelectedDatabaseItems([]);
          setDatabaseFilters({ category: [], uom: [], search: "" });
        }}
        databaseItems={databaseItems}
        loading={loadingDatabase}
        databaseFilters={databaseFilters}
        setDatabaseFilters={setDatabaseFilters}
        selectedDatabaseItems={selectedDatabaseItems}
        setSelectedDatabaseItems={setSelectedDatabaseItems}
        onAddItems={addDatabaseItems}
        formatCurrency={formatCurrency}
        formData={formData}
        parseSizeToCoverage={parseSizeToCoverage}
        isSFUnit={isSFUnit}
        isLFUnit={isLFUnit}
        getDatabaseFilterOptions={getDatabaseFilterOptions}
        netQuantities={netQuantities}
        netLoading={netLoading}
      />
    </div>
  );
}
